import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import useSeguroRuta from "../../hook/useSeguroRuta";
import usePermissions from "../../hook/usePermissions";
import { ContadoresSeguros } from "../../Service/Services.seguros";
import { GetTokenDecoded } from "../../utils/storeUsuario";
import { usePageSubtitulo } from "../../context/PageHeaderContext";
import { ChipEstado } from "../../Components/UI/Chip";
import { Aviso } from "../../Components/UI/Aviso";
import Boton from "../../Components/UI/Boton";
import { IconoChevronIzquierda } from "../../Components/UI/Iconos";
import Pagina from "./Pagina";
import "../../styles/Seguros.css";

/* ============================================================================
   SEGUROS-PLANES FASE 2 · ficha del seguro.

   El seguro es la entidad; tarifario, planes y promociones son caras suyas.
   Esta pantalla es el sitio donde el seguro se elige UNA vez —de hecho ya
   viene elegido en la URL— y no se vuelve a preguntar en ninguna de las
   pestañas.

   Cada pestaña es una RUTA de verdad (`NavLink`, no `useState`), así que se
   puede enlazar, recargar y usar el botón Atrás. Ese era el punto entero de
   la FASE 1.

   El contexto que baja a las pestañas es el seguro YA RESUELTO, para que no
   tengan que volver a pedirlo cada una. Ojo: es una comodidad de render, no
   la fuente de verdad. La fuente sigue siendo la URL — el constructor de plan
   vive FUERA de esta ficha (es pantalla completa) y resuelve su seguro por su
   cuenta con el mismo hook.
   ========================================================================== */

const CtxSeguro = createContext(null);

/** Devuelve { seguro, idseguro, contadores, recargarContadores }. */
export function useSeguroFicha() {
    const ctx = useContext(CtxSeguro);
    if (!ctx) {
        throw new Error("useSeguroFicha() solo puede usarse dentro de la ficha del seguro");
    }
    return ctx;
}

/**
 * Los contadores de las pestañas. Se piden UNA vez para toda la clínica y se
 * quedan aquí arriba: cambiar de pestaña no vuelve a pedirlos.
 *
 * Si la petición falla no se muestra ningún error: un contador es información
 * accesoria y no puede impedir trabajar. Se omite el número, nunca la pestaña
 * (§5 del encargo).
 */
function useContadores(idseguro) {
    const [contadores, setContadores] = useState(null);
    const [version, setVersion] = useState(0);

    useEffect(() => {
        let vivo = true;
        (async () => {
            const filas = await ContadoresSeguros(GetTokenDecoded().idclinica);
            if (!vivo) return;
            const mio = (filas || []).find((f) => Number(f.idseguro) === Number(idseguro));
            setContadores(mio || null);
        })();
        return () => {
            vivo = false;
        };
    }, [idseguro, version]);

    return [contadores, () => setVersion((v) => v + 1)];
}

function Pestana({ a, children, contador, final = false }) {
    return (
        <NavLink
            to={a}
            end={final}
            className={({ isActive }) => `sg-tab${isActive ? " sg-tab--activa" : ""}`}
        >
            {children}
            {Number.isFinite(contador) && <span className="sg-tab__contador">{contador}</span>}
        </NavLink>
    );
}

export default function SeguroFicha() {
    const navigate = useNavigate();
    const { idseguro, seguro, cargando, error, recargar } = useSeguroRuta();
    const { hasPermission } = usePermissions();
    const [contadores, recargarContadores] = useContadores(idseguro);

    usePageSubtitulo(seguro ? seguro.nombre : "");

    const valor = useMemo(
        () => ({ seguro, idseguro, contadores, recargarContadores }),
        [seguro, idseguro, contadores, recargarContadores]
    );

    const volver = () => navigate("/configuracion/seguros");

    if (cargando) {
        return (
            <Pagina className="sg-cargando" aria-busy="true">
                Cargando la aseguradora…
            </Pagina>
        );
    }

    // Los tres motivos de fallo se tratan igual a propósito: no confirmamos la
    // existencia de un id al que este usuario no tiene acceso.
    if (error || !seguro) {
        const esRed = error === "red";
        return (
            <Pagina>
                <button type="button" className="sg-volver" onClick={volver}>
                    <IconoChevronIzquierda size={15} />
                    Seguros
                </button>
                <Aviso
                    tipo={esRed ? "error" : "aviso"}
                    accion={esRed ? { label: "Reintentar", onClick: recargar } : { label: "Ir a Seguros", onClick: volver }}
                >
                    {esRed
                        ? "No se pudo cargar la aseguradora. Revisa la conexión e inténtalo de nuevo."
                        : "Esta aseguradora no existe o no tienes acceso a ella."}
                </Aviso>
            </Pagina>
        );
    }

    const activo = seguro.estado === "A";

    return (
        <CtxSeguro.Provider value={valor}>
            <Pagina>
                <button type="button" className="sg-volver" onClick={volver} data-testid="sg-volver">
                    <IconoChevronIzquierda size={15} />
                    Seguros
                </button>

                <header className="sg-ficha__cabecera">
                    <div className="sg-ficha__identidad">
                        <h2 className="sg-ficha__titulo">
                            {seguro.nombre}
                            <ChipEstado tono={activo ? "hecho" : "anulado"}>
                                {activo ? "Activo" : "Inactivo"}
                            </ChipEstado>
                        </h2>
                        {seguro.tipoSeguro?.tipo && (
                            <p className="sg-ficha__meta">{seguro.tipoSeguro.tipo}</p>
                        )}
                    </div>

                    {/* Editar y desactivar el seguro siguen viviendo en la pantalla
                        de Seguros de siempre: este rediseño no toca esos flujos.
                        Desde aquí se ofrece el camino, no una copia del formulario.
                        `/configuracion` no acepta un parámetro de sección (es estado
                        interno del componente, ver informe FASE 0 §6-R1), así que se
                        enlaza la pantalla, no la pestaña. */}
                    {hasPermission("seguros.edit") && (
                        <div className="sg-ficha__acciones">
                            <Boton onClick={() => navigate("/configuracion")}>
                                Editar datos
                            </Boton>
                        </div>
                    )}
                </header>

                <nav className="sg-tabs" aria-label="Secciones de la aseguradora">
                    <Pestana a={`/configuracion/seguros/${idseguro}`} final>
                        Resumen
                    </Pestana>
                    {hasPermission("procedimientosprecios.view") && (
                        <Pestana
                            a={`/configuracion/seguros/${idseguro}/tarifario`}
                            contador={contadores?.procedimientos}
                        >
                            Tarifario
                        </Pestana>
                    )}
                    {hasPermission("planes.view") && (
                        <Pestana
                            a={`/configuracion/seguros/${idseguro}/planes`}
                            contador={contadores?.planes}
                        >
                            Planes
                        </Pestana>
                    )}
                    {hasPermission("promociones.view") && (
                        <Pestana
                            a={`/configuracion/seguros/${idseguro}/promociones`}
                            contador={contadores?.promociones}
                        >
                            Promociones
                        </Pestana>
                    )}
                </nav>

                <Outlet />
            </Pagina>
        </CtxSeguro.Provider>
    );
}
